/**
 * Lighthouse attachment upload constraints.
 *
 * These MIRROR the backend `AttachmentValidationHelper` constants in
 * EagleEyeReviewService (MAX_FILE_SIZE_BYTES, ALLOWED_EXTENSIONS,
 * EXTENSION_TO_MIME_TYPE). Keep them in sync if the backend changes — the
 * backend is the source of truth and validates independently.
 *
 * Note: the backend imposes NO limit on the number of attachments per review.
 * The upload modal lets an auditor stage up to MAX_FILES_PER_UPLOAD files per session.
 */
export const ATTACHMENT_CONFIG = {
    /** Max size per file in bytes — 256 MB (backend MAX_FILE_SIZE_BYTES). */
    MAX_FILE_SIZE_BYTES: 268_435_456,

    /** Max number of files that can be uploaded in a single upload session (FE/product limit). */
    MAX_FILES_PER_UPLOAD: 5,

    /** Max file name length in characters (backend MAX_FILE_NAME_LENGTH). */
    MAX_FILE_NAME_LENGTH: 255,

    /** Allowed file extensions (backend ALLOWED_EXTENSIONS). */
    ALLOWED_EXTENSIONS: ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'xlsx', 'csv', 'txt'],

    /**
     * Extension → MIME type. MUST match the backend EXTENSION_TO_MIME_TYPE map;
     * the backend rejects the upload if the declared mimeType does not match the
     * extension.
     */
    EXTENSION_TO_MIME: {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        txt: 'text/plain',
    } as Record<string, string>,
} as const;

/**
 * Comma-separated `accept` attribute value for the file picker, derived from
 * ALLOWED_EXTENSIONS (e.g. ".pdf,.doc,...").
 */
export const ATTACHMENT_ACCEPT = ATTACHMENT_CONFIG.ALLOWED_EXTENSIONS
    .map(ext => `.${ext}`)
    .join(',');

/**
 * Formats a byte count as whole megabytes (MB) for user-facing messages, so the
 * "max size" wording always stays in sync with the byte limit above.
 */
export const formatMaxSizeMb = (bytes: number): number => Math.round(bytes / (1024 * 1024));

/**
 * Resolves the backend-expected MIME type for a file from its extension.
 * Falls back to the browser-provided File.type when the extension is unknown.
 */
export const resolveMimeType = (fileName: string, fallback: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return ATTACHMENT_CONFIG.EXTENSION_TO_MIME[ext] ?? fallback;
};
